/**
 * useCollisionDetection Hook
 * React integration for collision detection between aircraft and hangar walls
 *
 * Supports two paths:
 * 1. Synchronous (default): Runs collision checks on main thread
 * 2. Worker (opt-in via VITE_COLLISION_WORKER=true): Offloads to Web Worker
 */

import { useCallback, useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useCollisionStore } from '@/stores/collisionStore'
import { checkCollisions } from '@/services/collision'
import { WorkerCollisionAdapter } from '@/services/collision/worker/WorkerCollisionAdapter'
import type { HangarCollisionConfig, UnitCollisionConfig, SafetyMarginsConfig, CollisionResult } from '@/types/collision'
import type { PlacedAircraft3D } from '@/services/3d'

/** Feature flag: enable collision worker */
const USE_COLLISION_WORKER = import.meta.env.VITE_COLLISION_WORKER === 'true'

// Temporary prod diagnostic — remove after verification
console.log('[CollisionWorker:PROD] VITE_COLLISION_WORKER =', import.meta.env.VITE_COLLISION_WORKER, '→ USE_COLLISION_WORKER =', USE_COLLISION_WORKER)

/**
 * Hook parameters
 */
interface UseCollisionDetectionParams {
  /** Whether collision detection is enabled */
  enabled: boolean
  /** Array of hangar configurations for wall collision (legacy) */
  hangars: HangarCollisionConfig[]
  /** Array of unit configurations for edge-based wall collision */
  units?: UnitCollisionConfig[]
  /** Array of placed aircraft with position data */
  aircraft: PlacedAircraft3D[]
  /** Map of aircraft ID to Three.js Object3D */
  sceneObjects: Map<string, THREE.Object3D> | null
  /** Optional safety margins override from location settings */
  safetyMargins?: Partial<SafetyMarginsConfig>
  /** Callback when collision state changes */
  onCollisionChange?: (collisions: Set<string>) => void
}

/**
 * Hook return value
 */
interface UseCollisionDetectionReturn {
  /** Trigger a full collision check for all aircraft */
  checkAllCollisions: () => CollisionResult | null
  /** Check collision for a single aircraft (optimized for drag) */
  checkSingleAircraft: (allocationId: string) => CollisionResult | null
  /** Check if a specific aircraft is colliding */
  isColliding: (allocationId: string) => boolean
  /** Get collision details for a specific aircraft */
  getCollisionDetails: (allocationId: string) => ReturnType<typeof useCollisionStore.getState>['collisionDetails'] extends Map<string, infer T> ? T | undefined : never
  /** Current set of colliding aircraft IDs */
  collidingAircraftIds: Set<string>
  /** Whether collision detection is currently checking */
  isChecking: boolean
  /** Last check duration in milliseconds */
  lastCheckDuration: number
}

/**
 * React hook for collision detection integration
 */
export function useCollisionDetection({
  enabled,
  hangars,
  units,
  aircraft,
  sceneObjects,
  safetyMargins,
  onCollisionChange,
}: UseCollisionDetectionParams): UseCollisionDetectionReturn {
  const {
    collidingAircraftIds,
    isChecking,
    lastCheckDuration,
    setCollisions,
    mergeCollisions,
    setSafetyMargins,
    setEnabled,
    setChecking,
    isColliding,
    getCollisionDetails,
  } = useCollisionStore()

  // Track previous collision state for change detection
  const prevCollisionsRef = useRef<Set<string>>(new Set())

  // Worker adapter ref (persists across renders, created once)
  const workerAdapterRef = useRef<WorkerCollisionAdapter | null>(null)
  const workerInitializedRef = useRef(false)

  // Update enabled state
  useEffect(() => {
    setEnabled(enabled)
  }, [enabled, setEnabled])

  // Update safety margins from location settings
  useEffect(() => {
    if (safetyMargins) {
      setSafetyMargins(safetyMargins)
    }
  }, [safetyMargins, setSafetyMargins])

  // Notify on collision change
  useEffect(() => {
    // Compare with previous state
    const prevCollisions = prevCollisionsRef.current
    const hasChanged =
      prevCollisions.size !== collidingAircraftIds.size ||
      [...collidingAircraftIds].some((id) => !prevCollisions.has(id))

    if (hasChanged && onCollisionChange) {
      onCollisionChange(collidingAircraftIds)
    }

    prevCollisionsRef.current = new Set(collidingAircraftIds)
  }, [collidingAircraftIds, onCollisionChange])

  // Initialize worker adapter (if enabled)
  useEffect(() => {
    if (!USE_COLLISION_WORKER || !enabled) return

    const adapter = new WorkerCollisionAdapter()
    workerAdapterRef.current = adapter

    adapter.initialize().then(() => {
      console.log('[CollisionWorker:PROD] Worker adapter initialized ✓')
    })

    return () => {
      adapter.dispose()
      workerAdapterRef.current = null
      workerInitializedRef.current = false
    }
  }, [enabled])

  // Send geometry to worker when aircraft or scene objects change
  useEffect(() => {
    if (!USE_COLLISION_WORKER || !enabled || !sceneObjects || aircraft.length === 0) return

    const adapter = workerAdapterRef.current
    if (!adapter) return

    // Init geometry in worker, then trigger immediate collision check
    // Walls are already in worker (queued + flushed by adapter.initialize)
    adapter.initGeometry(aircraft, sceneObjects).then(() => {
      workerInitializedRef.current = true
      console.log(`[CollisionWorker:PROD] Worker geometry initialized: ${aircraft.length} aircraft — running initial check`)
      checkAllCollisions()
    })
  }, [enabled, aircraft, sceneObjects])

  // Send wall geometry to worker when configs change
  useEffect(() => {
    if (!USE_COLLISION_WORKER || !enabled) return

    const adapter = workerAdapterRef.current
    if (!adapter) return

    adapter.updateWalls(hangars, units ?? [])
  }, [enabled, hangars, units])

  /**
   * Perform a full collision check for all aircraft
   */
  const checkAllCollisions = useCallback((): CollisionResult | null => {
    if (!enabled || !sceneObjects || aircraft.length === 0) {
      return null
    }

    // Worker path: fire-and-forget async check
    if (USE_COLLISION_WORKER && workerAdapterRef.current) {
      workerAdapterRef.current.checkAllCollisions(aircraft, sceneObjects, hangars, units)
      return null // Result arrives async via store update
    }

    // Synchronous path (default)
    setChecking(true)

    try {
      const result = checkCollisions(aircraft, sceneObjects, hangars, undefined, units)
      setCollisions(result)
      return result
    } finally {
      setChecking(false)
    }
  }, [enabled, sceneObjects, aircraft, hangars, units, setChecking, setCollisions])

  /**
   * Check collision for a single aircraft (optimized for drag)
   * Uses proximity-based optimization
   */
  const checkSingleAircraft = useCallback(
    (allocationId: string): CollisionResult | null => {
      if (!enabled || !sceneObjects || aircraft.length === 0) {
        return null
      }

      // Worker path: fire-and-forget async check
      if (USE_COLLISION_WORKER && workerAdapterRef.current) {
        workerAdapterRef.current.checkSingleAircraft(
          allocationId,
          aircraft,
          sceneObjects,
          hangars,
          units
        )
        return null // Result arrives async via store update
      }

      // Synchronous path (default)
      setChecking(true)

      try {
        const result = checkCollisions(aircraft, sceneObjects, hangars, allocationId, units)
        // Merge results for partial checks, passing the moving aircraft ID
        // This enables collision chain clearing for aircraft dragged beyond proximity radius
        mergeCollisions(result, allocationId)
        return result
      } finally {
        setChecking(false)
      }
    },
    [enabled, sceneObjects, aircraft, hangars, units, setChecking, mergeCollisions]
  )

  // Run initial collision check when aircraft or hangars change (SYNC path only)
  // Worker path triggers immediately after initGeometry resolves (see effect above)
  useEffect(() => {
    if (USE_COLLISION_WORKER) return // Worker path handles its own initial check
    if (enabled && sceneObjects && aircraft.length > 0) {
      const timer = setTimeout(() => {
        checkAllCollisions()
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [enabled, aircraft.length, hangars.length, checkAllCollisions])

  return {
    checkAllCollisions,
    checkSingleAircraft,
    isColliding,
    getCollisionDetails,
    collidingAircraftIds,
    isChecking,
    lastCheckDuration,
  }
}

/**
 * Simplified hook that just returns collision state
 * For components that only need to read collision state
 */
export function useCollisionState() {
  const collidingAircraftIds = useCollisionStore((state) => state.collidingAircraftIds)
  const isColliding = useCollisionStore((state) => state.isColliding)
  const getCollisionDetails = useCollisionStore((state) => state.getCollisionDetails)
  const isEnabled = useCollisionStore((state) => state.isEnabled)

  return {
    collidingAircraftIds,
    isColliding,
    getCollisionDetails,
    isEnabled,
  }
}
